import JSZip from 'jszip';
import { deliverBlobToUser } from '../../../utils/downloadBlob';
import {
  normalizeTppSnapshot,
  type TppFileBlob,
  type TppSnapshot,
} from './tppLocalDb';
import { tppLocalStore } from './tppLocalStore';
import { buildTppWorkbookBuffer, parseTppWorkbookBuffer, TPP_XLSX_PATH } from './tppZipExcel';
import {
  buildTppZipReadme,
  TPP_META_JSON_PATH,
  TPP_PROOFS_PREFIX,
  ZIP_README_PATH,
} from './tppZipReadme';

export type TppZipMeta = {
  schemaVersion: 1;
  revision: number;
  updatedAt: string;
};

function proofArchivePath(storedFilename: string): string {
  return `${TPP_PROOFS_PREFIX}${storedFilename}`;
}

export async function buildTppZipArrayBuffer(snapshot: TppSnapshot): Promise<ArrayBuffer> {
  const snap = normalizeTppSnapshot(snapshot);
  const zip = new JSZip();

  zip.file(ZIP_README_PATH, buildTppZipReadme(snap));
  zip.file(TPP_XLSX_PATH, buildTppWorkbookBuffer(snap));

  const meta: TppZipMeta = {
    schemaVersion: 1,
    revision: snap.meta.revision,
    updatedAt: snap.meta.updatedAt,
  };
  zip.file(TPP_META_JSON_PATH, JSON.stringify(meta, null, 0));

  for (const ev of snap.events) {
    const key = ev.proofStoredFilename?.trim();
    if (!key) continue;
    const blob = snap.files[key];
    if (blob) {
      zip.file(proofArchivePath(key), new Uint8Array(blob.data));
    }
  }

  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
}

export async function buildTppZipBlob(snapshot: TppSnapshot): Promise<Blob> {
  const ab = await buildTppZipArrayBuffer(snapshot);
  return new Blob([ab], { type: 'application/zip' });
}

export async function exportTppZip(): Promise<Blob> {
  const snap = await tppLocalStore.exportSnapshot();
  return buildTppZipBlob(snap);
}

export async function parseTppZipBuffer(buffer: ArrayBuffer): Promise<TppSnapshot> {
  const zip = await JSZip.loadAsync(buffer);

  const xlsxEntry = zip.file(TPP_XLSX_PATH);
  if (!xlsxEntry) {
    throw new Error('zip_invalid_bundle');
  }

  const { cards, events, settings } = parseTppWorkbookBuffer(await xlsxEntry.async('arraybuffer'));

  let revision = 0;
  let updatedAt = new Date().toISOString();
  const metaEntry = zip.file(TPP_META_JSON_PATH);
  if (metaEntry) {
    try {
      const meta = JSON.parse(await metaEntry.async('string')) as TppZipMeta;
      if (meta.schemaVersion === 1) {
        revision = meta.revision ?? 0;
        updatedAt = meta.updatedAt ?? updatedAt;
      }
    } catch {
      /* meta.json 破損時は Excel のみで続行 */
    }
  }

  const files: Record<string, TppFileBlob> = {};
  for (const ev of events) {
    const key = ev.proofStoredFilename?.trim();
    if (!key) continue;
    const entry = zip.file(proofArchivePath(key));
    if (!entry) continue;
    const data = await entry.async('arraybuffer');
    files[key] = {
      mimeType: ev.proofMime ?? 'application/octet-stream',
      byteSize: data.byteLength,
      data,
    };
  }

  return normalizeTppSnapshot({
    version: 1,
    settings,
    meta: { schemaVersion: 1, revision, updatedAt },
    cards,
    events,
    files,
    counters: { card: 1, event: 1 },
  });
}

export function downloadTppZip(blob: Blob, filename = 'transit-pass-pool.zip'): void {
  deliverBlobToUser(blob, filename);
}

export async function parseTppZipFile(file: File): Promise<TppSnapshot> {
  return parseTppZipBuffer(await file.arrayBuffer());
}

export async function applyTppZipSnapshot(snapshot: TppSnapshot): Promise<void> {
  await tppLocalStore.replaceSnapshot(snapshot);
}

export async function importTppZip(file: File): Promise<void> {
  const snapshot = await parseTppZipFile(file);
  await applyTppZipSnapshot(snapshot);
}

export { ZIP_README_PATH, TPP_XLSX_PATH, TPP_META_JSON_PATH, TPP_PROOFS_PREFIX };
